import { useState, useEffect, useCallback, useRef } from 'react';
import StatusDot from '../components/StatusDot';
import Tag from '../components/Tag';
import { authFetch, getSession, submitJob, pollJob, isSignedIn, callBuildWithFiles } from '../api';

const API_BASE = 'https://invokeapi-production.up.railway.app';

const code = {
  sdk: (slug, key, tool) =>
`// npm install invokeapi
const InvokeAPI = require('invokeapi');
const client = new InvokeAPI('${key}');

const result = await client.call('${slug}', ${tool?.command || '{ type: "symbols" }'}${tool ? `,
  { tool: '${tool.slug}' }` : ''});
console.log(result);`,

  python: (slug, key, tool) =>
`import requests

res = requests.post(
    "${API_BASE}/v1/call/${slug}",
    headers={
        "Authorization": "Bearer ${key}"${tool ? `,
        "x-invokeapi-tool": "${tool.slug}"` : ''}
    },
    json=${tool?.command || '{"type": "symbols"}'}
)
print(res.json())`,

  js: (slug, key, tool) =>
`const res = await fetch("${API_BASE}/v1/call/${slug}", {
  method: "POST",
  headers: {
    "Authorization": "Bearer ${key}",
    "Content-Type": "application/json"${tool ? `,
    "x-invokeapi-tool": "${tool.slug}"` : ''}
  },
  body: JSON.stringify(${tool?.command || '{ type: "symbols" }'})
});
const data = await res.json();
console.log(data);`,

  curl: (slug, key, tool) =>
`curl -X POST ${API_BASE}/v1/call/${slug} \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json"${tool ? ` \\
  -H "x-invokeapi-tool: ${tool.slug}"` : ''} \\
  -d '${tool?.command || '{"type": "symbols"}'}'`,
};

// File upload code snippets
const fileCode = {
  python: (slug, key) =>
`import requests

res = requests.post(
    "${API_BASE}/v1/call/${slug}",
    headers={"Authorization": "Bearer ${key}"},
    files={"file": open("image.png", "rb")},
    data={"_params": '{"prompt": "Describe this image"}'}
)
print(res.json())`,

  js: (slug, key) =>
`const form = new FormData();
form.append("file", fileInput.files[0]);
form.append("_params", JSON.stringify({ prompt: "Describe this image" }));

const res = await fetch("${API_BASE}/v1/call/${slug}", {
  method: "POST",
  headers: { "Authorization": "Bearer ${key}" },
  body: form  // Don't set Content-Type — browser adds boundary
});
const data = await res.json();`,

  curl: (slug, key) =>
`curl -X POST ${API_BASE}/v1/call/${slug} \\
  -H "Authorization: Bearer ${key}" \\
  -F "file=@image.png" \\
  -F '_params={"prompt":"Describe this image"}'`,
};

// Category colors
const catColors = {
  query: { bg: 'rgba(59,130,246,0.10)', color: '#60a5fa', border: 'rgba(59,130,246,0.25)' },
  analysis: { bg: 'rgba(168,85,247,0.10)', color: '#c084fc', border: 'rgba(168,85,247,0.25)' },
  data: { bg: 'rgba(34,197,94,0.10)', color: '#4ade80', border: 'rgba(34,197,94,0.25)' },
  admin: { bg: 'rgba(234,179,8,0.10)', color: '#facc15', border: 'rgba(234,179,8,0.25)' },
  default: { bg: 'rgba(148,163,184,0.10)', color: '#94a3b8', border: 'rgba(148,163,184,0.25)' },
};

export default function BuildDetail({ slug, navigate }) {
  const [build, setBuild]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang]     = useState('sdk');
  const [copied, setCopied] = useState('');
  const [selectedTool, setSelectedTool] = useState(null);
  const [tab, setTab] = useState('overview'); // overview | library | ratings | playground

  // Playground state
  const [pgBody, setPgBody] = useState('{"type": "symbols"}');
  const [pgTool, setPgTool] = useState('');
  const [pgRunning, setPgRunning] = useState(false);
  const [pgResult, setPgResult] = useState(null);
  const [pgError, setPgError] = useState('');
  // Agent chat mode
  const [pgMode, setPgMode] = useState('agent'); // agent | json
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  const [myRating, setMyRating] = useState(null);
  const [myCallCount, setMyCallCount] = useState(0);
  const [ratingForm, setRatingForm] = useState({ rating: 0, review: '' });
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [ratingError, setRatingError] = useState('');

  // Jobs state
  const [jobParams, setJobParams] = useState('{}');
  const [jobSubmitting, setJobSubmitting] = useState(false);
  const [jobError, setJobError] = useState('');
  const [activeJob, setActiveJob] = useState(null);
  const [selectedJobType, setSelectedJobType] = useState(null);

  // File upload state
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadParams, setUploadParams] = useState('{}');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState('');

  // Build images
  const [buildImages, setBuildImages] = useState([]);
  const [imgUploading, setImgUploading] = useState(false);
  const [imgCaption, setImgCaption] = useState('');
  const fileInputRef = useRef(null);

  const [verification, setVerification] = useState(null);
  const [winWidth, setWinWidth] = useState(window.innerWidth);
  const onResize = useCallback(() => setWinWidth(window.innerWidth), []);
  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [onResize]);

  const session = getSession();
  const storedConsumerId = localStorage.getItem('am_consumer_id');
  const apiKey = 'YOUR_API_KEY';

  useEffect(() => {
    fetch(`/v1/builds/${slug}`)
      .then(r => r.json())
      .then(d => {
        setBuild(d);
        setLoading(false);
        if (d.job_types?.length > 0) setTab('jobs');
        else if (d.tools?.length > 0) setTab('library');
      })
      .catch(() => setLoading(false));
    // Load verification data
    fetch(`/v1/builds/${slug}/verification`)
      .then(r => r.json())
      .then(d => setVerification(d))
      .catch(() => {});
    // Load build images
    fetch(`/v1/builds/${slug}/images`)
      .then(r => r.json())
      .then(d => setBuildImages(d.images || []))
      .catch(() => {});
    // Load user's own rating if signed in
    if (session) {
      authFetch(`/v1/builds/${slug}/my-rating`)
        .then(r => r.json())
        .then(d => {
          setMyCallCount(d.call_count || 0);
          if (d.rating) {
            setMyRating(d.rating);
            setRatingForm({ rating: d.rating.rating, review: d.rating.review || '' });
          }
        })
        .catch(() => {});
    }
  }, [slug]); // eslint-disable-line

  async function submitRating() {
    setRatingError(''); setRatingSubmitting(true);
    try {
      const r = await authFetch(`/v1/builds/${slug}/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: ratingForm.rating, review: ratingForm.review || null }),
      });
      const d = await r.json();
      if (d.error) { setRatingError(d.error); }
      else {
        // Reload build to get updated ratings
        const buildRes = await fetch(`/v1/builds/${slug}`);
        setBuild(await buildRes.json());
        setMyRating({ rating: ratingForm.rating, review: ratingForm.review });
      }
    } catch { setRatingError('Failed to submit rating'); }
    setRatingSubmitting(false);
  }

  async function handleSubmitJob() {
    if (!selectedJobType) return;
    setJobError(''); setJobSubmitting(true);
    try {
      let params = {};
      try { params = JSON.parse(jobParams); } catch { setJobError('Invalid JSON parameters'); setJobSubmitting(false); return; }
      const data = await submitJob(slug, selectedJobType.slug, params);
      if (data.error) { setJobError(data.error); setJobSubmitting(false); return; }
      // Start polling
      setActiveJob({ id: data.job_id, status: 'running', progress: 0 });
      setJobSubmitting(false);
      pollActiveJob(data.job_id);
    } catch { setJobError('Failed to submit job'); setJobSubmitting(false); }
  }

  function pollActiveJob(jobId) {
    const interval = setInterval(async () => {
      try {
        const data = await pollJob(jobId);
        setActiveJob(data);
        if (data.status === 'completed' || data.status === 'failed' || data.status === 'timeout' || data.status === 'cancelled') {
          clearInterval(interval);
        }
      } catch { clearInterval(interval); }
    }, 3000);
  }

  function copy(text, label) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  }

  const s = {
    back: {
      color: 'var(--muted)', fontSize: 13, cursor: 'pointer',
      marginBottom: 28, display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 12px', borderRadius: 'var(--radius-xs)',
      background: 'transparent', border: 'none',
    },
    header: { marginBottom: 32 },
    h1: { fontSize: 32, fontWeight: 700, marginBottom: 8, letterSpacing: '-0.02em' },
    body: { display: 'grid', gridTemplateColumns: winWidth > 768 ? 'minmax(0, 1fr) minmax(250px, 300px)' : '1fr', gap: 32, alignItems: 'start' },
    section: { marginBottom: 32 },
    sectionTitle: {
      fontSize: 11, fontWeight: 600, color: 'var(--muted)',
      textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14,
    },
    desc: { color: 'var(--muted)', lineHeight: 1.8, fontSize: 15 },
    tags: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    langTabs: { display: 'flex', gap: 4, marginBottom: 12, background: 'var(--bg3)', borderRadius: 'var(--radius-xs)', padding: 3, width: 'fit-content' },
    langTab: (active) => ({
      padding: '5px 16px', borderRadius: 'var(--radius-xs)', fontSize: 12, fontWeight: 500,
      border: 'none',
      background: active ? 'var(--accent)' : 'transparent',
      color: active ? '#fff' : 'var(--muted)',
      cursor: 'pointer',
    }),
    codeBlock: {
      background: 'var(--bg)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)', padding: '16px 18px',
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: 12.5,
      lineHeight: 1.8, color: 'var(--accent2)',
      whiteSpace: 'pre', overflowX: 'auto', position: 'relative',
    },
    copyBtn: {
      position: 'absolute', top: 10, right: 10,
      padding: '4px 12px', fontSize: 11, fontWeight: 500,
      border: '1px solid var(--border)', borderRadius: 'var(--radius-xs)',
      background: 'var(--bg2)', color: 'var(--muted)', cursor: 'pointer',
    },
    sidebar: {
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: 24,
      position: 'sticky', top: 80,
    },
    price: {
      fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em',
      background: 'linear-gradient(135deg, var(--accent2), var(--accent))',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      marginBottom: 4,
    },
    priceSub: { fontSize: 13, color: 'var(--muted)', marginBottom: 20 },
    callBtn: {
      width: '100%', padding: '12px', borderRadius: 'var(--radius-sm)',
      border: 'none', background: 'linear-gradient(135deg, var(--accent), #6558d4)',
      color: '#fff', fontWeight: 600, fontSize: 15, marginBottom: 10,
      cursor: 'pointer', boxShadow: '0 2px 12px rgba(124,111,239,0.3)',
    },
    secondBtn: {
      width: '100%', padding: '10px', borderRadius: 'var(--radius-sm)',
      border: '1px solid var(--border)', background: 'transparent',
      color: 'var(--text)', fontWeight: 500, fontSize: 14,
      marginBottom: 20, cursor: 'pointer',
    },
    divider: { borderTop: '1px solid var(--border)', margin: '18px 0' },
    statRow: {
      display: 'flex', justifyContent: 'space-between',
      fontSize: 13, marginBottom: 10, alignItems: 'center',
    },
    statLabel: { color: 'var(--muted)' },
    statVal: { fontWeight: 500 },
    // Tabs
    tabBar: {
      display: 'flex', gap: 0, marginBottom: 28,
      borderBottom: '1px solid var(--border)',
    },
    tab: (active) => ({
      padding: '10px 20px', fontSize: 14, fontWeight: active ? 600 : 400,
      color: active ? 'var(--text)' : 'var(--muted)',
      borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
      cursor: 'pointer', background: 'none', border: 'none',
      marginBottom: -1,
    }),
    // Tool cards
    toolCard: (selected) => ({
      background: selected ? 'rgba(124,111,239,0.06)' : 'var(--bg2)',
      border: `1px solid ${selected ? 'rgba(124,111,239,0.3)' : 'var(--border)'}`,
      borderRadius: 'var(--radius-sm)', padding: '16px 18px',
      marginBottom: 10, cursor: 'pointer',
      transition: 'all 0.15s ease',
    }),
    toolHeader: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      marginBottom: 6,
    },
    toolName: { fontWeight: 600, fontSize: 15 },
    toolSlug: { fontFamily: 'monospace', fontSize: 12, color: 'var(--muted)', marginTop: 2 },
    toolDesc: { fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 8 },
    toolPrice: {
      fontSize: 16, fontWeight: 700,
      background: 'linear-gradient(135deg, var(--accent2), var(--accent))',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    },
    toolCommand: {
      fontFamily: 'monospace', fontSize: 12, color: 'var(--accent2)',
      background: 'var(--bg)', padding: '8px 12px',
      borderRadius: 'var(--radius-xs)', border: '1px solid var(--border)',
      marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
    },
    catBadge: (cat) => {
      const c = catColors[cat] || catColors.default;
      return {
        display: 'inline-block', padding: '2px 10px', borderRadius: 99,
        fontSize: 11, fontWeight: 500, background: c.bg, color: c.color,
        border: `1px solid ${c.border}`,
      };
    },
    examples: { display: 'flex', flexDirection: 'column', gap: 8 },
    example: {
      padding: '12px 16px', background: 'var(--bg3)',
      borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
      fontSize: 13, color: 'var(--text)', cursor: 'pointer',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    },
  };

  if (loading) return (
    <div style={{ color: 'var(--muted)', padding: '60px 0', textAlign: 'center' }}>Loading...</div>
  );
  if (!build || build.error) return (
    <div style={{ color: 'var(--muted)', padding: '60px 0', textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>{'\u{1F6AB}'}</div>
      Build not found.
    </div>
  );

  const tools = build.tools || [];
  const jobTypes = build.job_types || [];
  const hasTools = tools.length > 0;
  const hasJobs = jobTypes.length > 0;
  const snippet = code[lang](slug, apiKey, selectedTool);

  // Group tools by category
  const categories = {};
  tools.forEach(t => {
    const cat = t.category || 'default';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(t);
  });

  return (
    <div>
      <button style={s.back} onClick={() => navigate('marketplace')}>
        {'\u2190'} Back to marketplace
      </button>

      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 6 }}>
          <h1 style={s.h1}>{build.name}</h1>
          <StatusDot isLive={build.is_live} uptime={build.uptime_pct} />
        </div>
        <div style={{ fontSize: 14, color: 'var(--muted)' }}>
          Endpoint: <code style={{
            fontFamily: 'monospace', fontSize: 13, color: 'var(--accent2)',
            background: 'var(--bg3)', padding: '2px 8px', borderRadius: 4,
          }}>/v1/call/{slug}</code>
          {hasTools && (
            <span style={{ marginLeft: 12, fontSize: 13, color: 'var(--muted)' }}>
              {tools.length} tool{tools.length !== 1 ? 's' : ''} available
            </span>
          )}
        </div>
        {/* Share bar */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
          <button
            onClick={() => { navigator.clipboard.writeText(`${API_BASE}/share/${slug}`); setCopied('share'); }}
            style={{ padding: '6px 14px', borderRadius: 8, background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            {copied === 'share' ? 'Link Copied!' : 'Share Build'}
          </button>
          <a
            href={`https://x.com/intent/tweet?text=${encodeURIComponent(`Check out ${build.name} on invokeAPI`)}&url=${encodeURIComponent(`${API_BASE}/share/${slug}`)}`}
            target="_blank" rel="noopener noreferrer"
            style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--muted)', fontSize: 12, fontWeight: 500, textDecoration: 'none' }}
          >X</a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${API_BASE}/share/${slug}`)}`}
            target="_blank" rel="noopener noreferrer"
            style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--muted)', fontSize: 12, fontWeight: 500, textDecoration: 'none' }}
          >in</a>
        </div>
      </div>

      <div style={s.body}>
        <div>
          {/* Tabs */}
          <div style={s.tabBar}>
            {hasTools && (
              <button style={s.tab(tab === 'library')} onClick={() => setTab('library')}>
                Library ({tools.length})
              </button>
            )}
            {hasJobs && (
              <button style={s.tab(tab === 'jobs')} onClick={() => setTab('jobs')}>
                Jobs ({jobTypes.length})
              </button>
            )}
            {build.accepts_files && (
              <button style={s.tab(tab === 'files')} onClick={() => setTab('files')}>
                Upload Files
              </button>
            )}
            <button style={s.tab(tab === 'playground')} onClick={() => setTab('playground')}>
              Playground
            </button>
            <button style={s.tab(tab === 'overview')} onClick={() => setTab('overview')}>
              Overview
            </button>
            <button style={s.tab(tab === 'ratings')} onClick={() => setTab('ratings')}>
              Reviews {build.rating_summary?.count > 0 ? `(${build.rating_summary.count})` : ''}
            </button>
          </div>

          {/* ── Library tab ── */}
          {tab === 'library' && hasTools && (
            <>
              <div style={s.section}>
                <div style={s.sectionTitle}>Available Tools</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18, lineHeight: 1.6 }}>
                  Each tool has its own price. Click a tool to see its usage example.
                  {build.price_range && (
                    <span> Prices range from <strong style={{ color: 'var(--accent2)' }}>${build.price_range.min.toFixed(2)}</strong> to <strong style={{ color: 'var(--accent2)' }}>${build.price_range.max.toFixed(2)}</strong> per call.</span>
                  )}
                </div>

                {Object.entries(categories).map(([cat, catTools]) => (
                  <div key={cat} style={{ marginBottom: 20 }}>
                    {Object.keys(categories).length > 1 && (
                      <div style={{ marginBottom: 10 }}>
                        <span style={s.catBadge(cat)}>{cat}</span>
                      </div>
                    )}
                    {catTools.map(tool => (
                      <div key={tool.id} style={s.toolCard(selectedTool?.id === tool.id)}
                           onClick={() => setSelectedTool(selectedTool?.id === tool.id ? null : tool)}>
                        <div style={s.toolHeader}>
                          <div>
                            <div style={s.toolName}>{tool.name}</div>
                            <div style={s.toolSlug}>{tool.slug}</div>
                          </div>
                          <div style={s.toolPrice}>${tool.price_per_call.toFixed(2)}</div>
                        </div>
                        {tool.description && <div style={s.toolDesc}>{tool.description}</div>}
                        {selectedTool?.id === tool.id && tool.command && (
                          <div style={s.toolCommand}>
                            <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4, fontFamily: 'sans-serif', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Example payload</div>
                            {tool.command}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Code example — updates with selected tool */}
              <div style={s.section}>
                <div style={s.sectionTitle}>
                  {selectedTool ? `Call "${selectedTool.name}"` : 'Quick start'}
                </div>

                {!storedConsumerId && (
                  <div style={{
                    fontSize: 13, padding: '12px 16px',
                    background: 'rgba(124,111,239,0.06)',
                    border: '1px solid rgba(124,111,239,0.2)',
                    borderRadius: 'var(--radius-sm)', marginBottom: 16,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span>{'\u{1F511}'}</span>
                    <span>
                      <span style={{ cursor: 'pointer', color: 'var(--accent2)', fontWeight: 600 }}
                        onClick={() => navigate('getkey')}>Get an API key</span>
                      <span style={{ color: 'var(--muted)' }}> to start calling</span>
                    </span>
                  </div>
                )}

                <div style={s.langTabs}>
                  {['sdk', 'curl', 'python', 'js'].map(l => (
                    <button key={l} style={s.langTab(lang === l)} onClick={() => setLang(l)}>
                      {l === 'js' ? 'JavaScript' : l === 'sdk' ? 'SDK' : l.charAt(0).toUpperCase() + l.slice(1)}
                    </button>
                  ))}
                </div>
                <div style={s.codeBlock}>
                  {snippet}
                  <button style={s.copyBtn} onClick={() => copy(snippet, 'code')}>
                    {copied === 'code' ? '\u2713 copied' : 'Copy'}
                  </button>
                </div>

                {selectedTool && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
                    This call will charge <strong style={{ color: 'var(--accent2)' }}>${selectedTool.price_per_call.toFixed(2)}</strong> per request.
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Jobs tab ── */}
          {tab === 'jobs' && hasJobs && (
            <>
              <div style={s.section}>
                <div style={s.sectionTitle}>Available Jobs</div>
                <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18, lineHeight: 1.6 }}>
                  Jobs are async tasks that run in the background. Submit a job, then poll for results. Prices are per execution.
                </div>

                {jobTypes.map(jt => (
                  <div key={jt.slug} style={{
                    ...s.toolCard(selectedJobType?.slug === jt.slug),
                    cursor: 'pointer',
                  }} onClick={() => {
                    setSelectedJobType(selectedJobType?.slug === jt.slug ? null : jt);
                    setActiveJob(null); setJobError('');
                    if (jt.example_params) setJobParams(JSON.stringify(jt.example_params, null, 2));
                    else setJobParams('{}');
                  }}>
                    <div style={s.toolHeader}>
                      <div>
                        <div style={s.toolName}>{jt.name}</div>
                        <div style={s.toolSlug}>{jt.slug}</div>
                      </div>
                      <div style={s.toolPrice}>${jt.price.toFixed(2)}</div>
                    </div>
                    {jt.description && <div style={s.toolDesc}>{jt.description}</div>}
                    <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--muted)' }}>
                      <span>Timeout: {Math.round(jt.timeout_ms / 60000)}min</span>
                      {jt.category && jt.category !== 'default' && <span style={s.catBadge(jt.category)}>{jt.category}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Job submission form */}
              {selectedJobType && (
                <div style={s.section}>
                  <div style={s.sectionTitle}>Run "{selectedJobType.name}"</div>

                  {!isSignedIn() ? (
                    <div style={{
                      fontSize: 13, padding: '16px 20px',
                      background: 'rgba(124,111,239,0.06)',
                      border: '1px solid rgba(124,111,239,0.2)',
                      borderRadius: 'var(--radius-sm)',
                    }}>
                      <span style={{ cursor: 'pointer', color: 'var(--accent2)', fontWeight: 600 }}
                        onClick={() => navigate('signin')}>Sign in</span>
                      <span style={{ color: 'var(--muted)' }}> to submit jobs</span>
                    </div>
                  ) : activeJob ? (
                    /* Active job status */
                    <div style={{
                      padding: 20, background: 'var(--bg2)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>Job {activeJob.id?.slice(0, 8)}...</span>
                        <span style={{
                          padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                          background: activeJob.status === 'completed' ? 'rgba(34,197,94,0.1)' :
                                     activeJob.status === 'running' ? 'rgba(59,130,246,0.1)' :
                                     activeJob.status === 'failed' || activeJob.status === 'timeout' ? 'rgba(239,68,68,0.1)' : 'var(--bg3)',
                          color: activeJob.status === 'completed' ? 'var(--green)' :
                                 activeJob.status === 'running' ? '#60a5fa' :
                                 activeJob.status === 'failed' || activeJob.status === 'timeout' ? '#ef4444' : 'var(--muted)',
                        }}>{activeJob.status}</span>
                      </div>

                      {/* Progress bar */}
                      {(activeJob.status === 'running' || activeJob.status === 'pending') && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden', marginBottom: 4 }}>
                            <div style={{
                              width: `${activeJob.progress || 0}%`, height: '100%',
                              background: 'linear-gradient(90deg, var(--accent), #6558d4)',
                              borderRadius: 3, transition: 'width 0.5s ease',
                            }} />
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                            {activeJob.progress || 0}%{activeJob.progress_msg ? ` — ${activeJob.progress_msg}` : ''}
                          </div>
                        </div>
                      )}

                      {/* Result */}
                      {activeJob.status === 'completed' && activeJob.result && (
                        <div style={{
                          background: 'var(--bg)', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-xs)', padding: '12px 14px',
                          fontFamily: 'monospace', fontSize: 12, color: 'var(--accent2)',
                          maxHeight: 400, overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                        }}>
                          {typeof activeJob.result === 'string' ? activeJob.result : JSON.stringify(activeJob.result, null, 2)}
                        </div>
                      )}

                      {/* Error */}
                      {(activeJob.status === 'failed' || activeJob.status === 'timeout') && (
                        <div style={{ fontSize: 13, color: '#ef4444', padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 'var(--radius-xs)' }}>
                          {activeJob.error || 'Job failed'}
                        </div>
                      )}

                      {/* New job button */}
                      {(activeJob.status === 'completed' || activeJob.status === 'failed' || activeJob.status === 'timeout') && (
                        <button onClick={() => setActiveJob(null)} style={{
                          marginTop: 12, padding: '8px 16px', borderRadius: 'var(--radius-xs)',
                          border: '1px solid var(--border)', background: 'transparent',
                          color: 'var(--text)', fontSize: 13, cursor: 'pointer',
                        }}>Run another job</button>
                      )}
                    </div>
                  ) : (
                    /* Job form */
                    <div style={{
                      padding: 20, background: 'var(--bg2)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)', marginBottom: 4 }}>Parameters (JSON)</div>
                      <textarea
                        style={{
                          width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-xs)',
                          border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)',
                          fontFamily: 'monospace', fontSize: 12, minHeight: 100, resize: 'vertical',
                          boxSizing: 'border-box', marginBottom: 12,
                        }}
                        value={jobParams}
                        onChange={e => setJobParams(e.target.value)}
                      />
                      {jobError && <div style={{ color: '#ef4444', fontSize: 12, marginBottom: 8 }}>{jobError}</div>}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                          Cost: <strong style={{ color: 'var(--accent2)' }}>${selectedJobType.price.toFixed(2)}</strong>
                          {' '}| Timeout: {Math.round(selectedJobType.timeout_ms / 60000)}min
                        </span>
                        <button onClick={handleSubmitJob} disabled={jobSubmitting} style={{
                          padding: '10px 24px', borderRadius: 'var(--radius-xs)', border: 'none',
                          background: 'linear-gradient(135deg, var(--accent), #6558d4)',
                          color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                        }}>
                          {jobSubmitting ? 'Submitting...' : 'Run Job'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── Overview tab ── */}
          {tab === 'overview' && (
            <>
              {/* Description */}
              <div style={s.section}>
                <div style={s.sectionTitle}>About</div>
                <p style={s.desc}>{build.description}</p>
              </div>

              {/* Build Images Gallery */}
              {(buildImages.length > 0 || isSignedIn()) && (
                <div style={s.section}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={s.sectionTitle}>Preview</div>
                    {isSignedIn() && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={imgUploading}
                        style={{ padding: '4px 12px', borderRadius: 6, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--muted)', fontSize: 12, cursor: 'pointer' }}
                      >
                        {imgUploading ? 'Uploading...' : '+ Add Image'}
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
                      setImgUploading(true);
                      try {
                        const reader = new FileReader();
                        reader.onload = async () => {
                          const base64 = reader.result;
                          const caption = prompt('Add a caption (optional):') || '';
                          const res = await fetch(`/v1/builds/${slug}/images`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ image: base64, caption, filename: file.name }),
                          });
                          const d = await res.json();
                          if (d.image) {
                            setBuildImages(prev => [...prev, { ...d.image, _ts: Date.now() }]);
                          } else {
                            alert(d.error || 'Upload failed');
                          }
                          setImgUploading(false);
                        };
                        reader.readAsDataURL(file);
                      } catch { setImgUploading(false); alert('Upload failed'); }
                      e.target.value = '';
                    }}
                  />
                  {buildImages.length > 0 && (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                      {buildImages.map(img => (
                        <div key={img.id} style={{ position: 'relative', flex: '1 1 280px', maxWidth: buildImages.length === 1 ? '100%' : '48%' }}>
                          <img
                            src={`/v1/builds/${slug}/images/${img.id}`}
                            alt={img.caption || build.name}
                            style={{ width: '100%', borderRadius: 10, border: '1px solid var(--border)' }}
                          />
                          {img.caption && <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, textAlign: 'center' }}>{img.caption}</p>}
                          {isSignedIn() && (
                            <button
                              onClick={async () => {
                                if (!window.confirm('Delete this image?')) return;
                                await fetch(`/v1/builds/${slug}/images/${img.id}`, { method: 'DELETE' });
                                setBuildImages(prev => prev.filter(i => i.id !== img.id));
                              }}
                              style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.7)', border: 'none', color: '#f87171', borderRadius: 4, padding: '2px 6px', fontSize: 11, cursor: 'pointer' }}
                            >x</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {buildImages.length === 0 && isSignedIn() && (
                    <p style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic', marginTop: 4 }}>No preview images yet. Upload screenshots or visuals of your build.</p>
                  )}
                </div>
              )}

              {/* Tags */}
              {build.tags?.length > 0 && (
                <div style={s.section}>
                  <div style={s.sectionTitle}>Categories</div>
                  <div style={s.tags}>{build.tags.map(t => <Tag key={t} label={t} />)}</div>
                </div>
              )}

              {/* Example queries */}
              {build.example_queries?.length > 0 && (
                <div style={s.section}>
                  <div style={s.sectionTitle}>Example queries</div>
                  <div style={s.examples}>
                    {build.example_queries.map((q, i) => (
                      <div key={i} style={s.example} onClick={() => copy(q, `ex${i}`)}>
                        <span>{q}</span>
                        <span style={{ fontSize: 11, color: copied === `ex${i}` ? 'var(--green)' : 'var(--muted)' }}>
                          {copied === `ex${i}` ? '\u2713 copied' : 'copy'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Code example (no tool selected) */}
              <div style={s.section}>
                <div style={s.sectionTitle}>Quick start</div>

                {!storedConsumerId && (
                  <div style={{
                    fontSize: 13, padding: '12px 16px',
                    background: 'rgba(124,111,239,0.06)',
                    border: '1px solid rgba(124,111,239,0.2)',
                    borderRadius: 'var(--radius-sm)', marginBottom: 16,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span>{'\u{1F511}'}</span>
                    <span>
                      <span style={{ cursor: 'pointer', color: 'var(--accent2)', fontWeight: 600 }}
                        onClick={() => navigate('getkey')}>Get an API key</span>
                      <span style={{ color: 'var(--muted)' }}> to replace YOUR_API_KEY</span>
                    </span>
                  </div>
                )}

                <div style={s.langTabs}>
                  {['sdk', 'curl', 'python', 'js'].map(l => (
                    <button key={l} style={s.langTab(lang === l)} onClick={() => setLang(l)}>
                      {l === 'js' ? 'JavaScript' : l === 'sdk' ? 'SDK' : l.charAt(0).toUpperCase() + l.slice(1)}
                    </button>
                  ))}
                </div>
                <div style={s.codeBlock}>
                  {snippet}
                  <button style={s.copyBtn} onClick={() => copy(snippet, 'code')}>
                    {copied === 'code' ? '\u2713 copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ── Files tab ── */}
          {tab === 'files' && build.accepts_files && (
            <div style={s.section}>
              <div style={s.sectionTitle}>Upload Files</div>
              {build.file_instructions && (
                <div style={{ padding: '14px 18px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.20)', borderRadius: 'var(--radius-sm)', marginBottom: 20, fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>
                  {build.file_instructions}
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                {build.accepted_file_types?.length > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    Accepted: {build.accepted_file_types.join(', ')}
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  Max size: {(build.max_file_size / 1048576).toFixed(0)}MB
                </div>
              </div>

              {/* Drop zone */}
              <div
                onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--accent)'; }}
                onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                onDrop={e => {
                  e.preventDefault(); e.currentTarget.style.borderColor = 'var(--border)';
                  const dropped = Array.from(e.dataTransfer.files);
                  setUploadFiles(prev => [...prev, ...dropped]);
                }}
                style={{ border: '2px dashed var(--border)', borderRadius: 'var(--radius-sm)', padding: '30px 20px', textAlign: 'center', marginBottom: 16, cursor: 'pointer', transition: 'border-color 0.2s' }}
                onClick={() => { const inp = document.createElement('input'); inp.type = 'file'; inp.multiple = true; inp.onchange = e => setUploadFiles(prev => [...prev, ...Array.from(e.target.files)]); inp.click(); }}
              >
                <div style={{ fontSize: 28, marginBottom: 8 }}>+</div>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>Drop files here or click to browse</div>
              </div>

              {/* File list */}
              {uploadFiles.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {uploadFiles.map((f, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--bg2)', borderRadius: 'var(--radius-sm)', marginBottom: 4 }}>
                      <span style={{ fontSize: 13 }}>{f.name} <span style={{ color: 'var(--muted)', fontSize: 11 }}>({(f.size / 1024).toFixed(1)}KB)</span></span>
                      <button onClick={() => setUploadFiles(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 16 }}>x</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Optional params */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>Additional params (JSON, optional)</div>
                <textarea value={uploadParams} onChange={e => setUploadParams(e.target.value)}
                  rows={3} style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 12, fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>

              {/* Submit */}
              <button disabled={uploading || uploadFiles.length === 0 || !isSignedIn()}
                onClick={async () => {
                  setUploading(true); setUploadError(''); setUploadResult(null);
                  try {
                    let params = {};
                    try { params = JSON.parse(uploadParams); } catch {}
                    const res = await callBuildWithFiles(build.slug, params, uploadFiles);
                    const ct = res.headers.get('content-type') || '';
                    if (ct.startsWith('image/')) {
                      const blob = await res.blob();
                      setUploadResult({ type: 'image', url: URL.createObjectURL(blob) });
                    } else {
                      const data = await res.json();
                      setUploadResult({ type: 'json', data });
                    }
                  } catch (e) { setUploadError(e.message); }
                  setUploading(false);
                }}
                style={{ padding: '10px 24px', borderRadius: 'var(--radius-sm)', border: 'none', background: uploading ? 'var(--muted)' : 'var(--accent)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: uploading || !isSignedIn() ? 'not-allowed' : 'pointer' }}>
                {!isSignedIn() ? 'Sign in to upload' : uploading ? 'Uploading...' : `Upload & Call ($${build.price_per_call})`}
              </button>

              {uploadError && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 10 }}>{uploadError}</div>}
              {uploadResult?.type === 'image' && (
                <div style={{ marginTop: 16 }}><img src={uploadResult.url} alt="Result" style={{ maxWidth: '100%', borderRadius: 'var(--radius-sm)' }} /></div>
              )}
              {uploadResult?.type === 'json' && (
                <pre style={{ marginTop: 16, padding: 14, background: 'var(--bg2)', borderRadius: 'var(--radius-sm)', fontSize: 12, fontFamily: 'monospace', color: 'var(--accent2)', whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto' }}>
                  {JSON.stringify(uploadResult.data, null, 2)}
                </pre>
              )}

              {/* Code examples */}
              <div style={{ marginTop: 24 }}>
                <div style={s.sectionTitle}>Code Examples</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  {['curl', 'python', 'js'].map(l => (
                    <button key={l} onClick={() => setLang(l)} style={{ ...s.langBtn, ...(lang === l ? s.langBtnActive : {}) }}>{l}</button>
                  ))}
                </div>
                <pre style={s.codeBlock}>{fileCode[lang](build.slug, 'YOUR_API_KEY')}</pre>
              </div>
            </div>
          )}

          {/* ── Playground tab ── */}
          {tab === 'playground' && (
            <>
              <div style={s.section}>
                {/* Mode toggle */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={s.sectionTitle}>Playground</div>
                  <div style={{ display: 'flex', gap: 0, borderRadius: 'var(--radius-xs)', overflow: 'hidden', border: '1px solid var(--border)' }}>
                    <button onClick={() => setPgMode('agent')} style={{
                      padding: '6px 16px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      background: pgMode === 'agent' ? 'var(--accent)' : 'var(--bg2)', color: pgMode === 'agent' ? '#fff' : 'var(--muted)',
                    }}>Agent</button>
                    <button onClick={() => setPgMode('json')} style={{
                      padding: '6px 16px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      background: pgMode === 'json' ? 'var(--accent)' : 'var(--bg2)', color: pgMode === 'json' ? '#fff' : 'var(--muted)',
                    }}>JSON</button>
                  </div>
                </div>

                {/* ── Agent Chat mode ── */}
                {pgMode === 'agent' && (
                  <div style={{
                    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                    background: 'var(--bg)', overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    height: 480,
                  }}>
                    {/* Chat messages */}
                    <div style={{
                      flex: 1, overflowY: 'auto', padding: '16px',
                      display: 'flex', flexDirection: 'column', gap: 12,
                    }}>
                      {chatMessages.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--muted)' }}>
                          <div style={{ fontSize: 28, marginBottom: 12 }}>💬</div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                            Talk to {build?.name}
                          </div>
                          <div style={{ fontSize: 12.5, lineHeight: 1.6, maxWidth: 360, margin: '0 auto' }}>
                            Ask anything in plain English. The agent will pick the right tool and explain the results.
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginTop: 16 }}>
                            {(build?.slug === 'stockforge' ? ['What is Apple trading at?', 'Compare MSFT vs GOOGL', 'Show Tesla history'] :
                              build?.slug === 'openmeteo' ? ['Weather in Tokyo?', 'Air quality in NYC', '7-day forecast for London'] :
                              build?.slug === 'sslscanner' ? ['Scan google.com', 'Check SSL for github.com', 'DNS records for invoke-api.com'] :
                              build?.slug === 'piiredaction' ? ['Is this safe? john@test.com, SSN 123-45-6789', 'What PII types can you detect?'] :
                              build?.slug === 'geolocation' ? ['Where is the Eiffel Tower?', 'Distance NYC to LA', 'Locate IP 8.8.8.8'] :
                              build?.slug === 'quickchart' ? ['Make a bar chart of Q1 sales', 'QR code for my website'] :
                              build?.slug === 'imagesorcery' ? ['What formats do you support?', 'How does optimization work?'] :
                              build?.slug === 'ffmpeg' ? ['What can you do?', 'How do I convert video to mp3?'] :
                              ['What can you do?', 'Show me an example']
                            ).map(q => (
                              <button key={q} onClick={() => { setChatInput(q); }} style={{
                                padding: '5px 12px', borderRadius: 99, fontSize: 11.5, cursor: 'pointer',
                                border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--muted)',
                                transition: 'all 0.15s',
                              }}>{q}</button>
                            ))}
                          </div>
                        </div>
                      )}

                      {chatMessages.map((msg, i) => (
                        <div key={i} style={{
                          display: 'flex', flexDirection: 'column',
                          alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                          maxWidth: '85%', alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        }}>
                          <div style={{
                            padding: '10px 14px', borderRadius: 12,
                            background: msg.role === 'user'
                              ? 'linear-gradient(135deg, var(--accent), #6558d4)'
                              : 'var(--bg2)',
                            color: msg.role === 'user' ? '#fff' : 'var(--text)',
                            fontSize: 13, lineHeight: 1.6,
                            borderBottomRightRadius: msg.role === 'user' ? 4 : 12,
                            borderBottomLeftRadius: msg.role === 'user' ? 12 : 4,
                            whiteSpace: 'pre-wrap',
                          }}>
                            {msg.content}
                          </div>
                          {/* Tool badge + raw data toggle */}
                          {msg.tool_used && (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                              <span style={{
                                fontSize: 10, padding: '2px 8px', borderRadius: 99,
                                background: 'rgba(124,111,239,0.1)', color: 'var(--accent)',
                                fontFamily: 'monospace',
                              }}>
                                {msg.tool_name || msg.tool_used}
                              </span>
                              {msg.cost && (
                                <span style={{ fontSize: 10, color: 'var(--muted)' }}>${msg.cost}</span>
                              )}
                              {msg.raw_data && (
                                <button onClick={() => {
                                  setChatMessages(prev => prev.map((m, j) => j === i ? { ...m, showRaw: !m.showRaw } : m));
                                }} style={{
                                  fontSize: 10, padding: '2px 8px', borderRadius: 99, cursor: 'pointer',
                                  border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)',
                                }}>
                                  {msg.showRaw ? 'Hide JSON' : 'Show JSON'}
                                </button>
                              )}
                            </div>
                          )}
                          {msg.showRaw && msg.raw_data && (
                            <pre style={{
                              marginTop: 6, padding: '8px 10px', borderRadius: 'var(--radius-xs)',
                              background: 'var(--bg)', border: '1px solid var(--border)',
                              color: 'var(--accent2)', fontSize: 11, maxHeight: 200, overflow: 'auto',
                              fontFamily: "'JetBrains Mono', monospace", whiteSpace: 'pre-wrap',
                              width: '100%',
                            }}>
                              {JSON.stringify(msg.raw_data, null, 2)}
                            </pre>
                          )}
                        </div>
                      ))}

                      {chatLoading && (
                        <div style={{ alignSelf: 'flex-start', padding: '10px 14px', borderRadius: 12, background: 'var(--bg2)', borderBottomLeftRadius: 4 }}>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {[0,1,2].map(i => (
                              <span key={i} style={{
                                width: 6, height: 6, borderRadius: '50%', background: 'var(--muted)',
                                animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                                opacity: 0.4,
                              }} />
                            ))}
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>

                    {/* Chat input */}
                    <div style={{
                      borderTop: '1px solid var(--border)', padding: '12px',
                      display: 'flex', gap: 8, background: 'var(--bg2)',
                    }}>
                      <input
                        value={chatInput}
                        onChange={e => setChatInput(e.target.value)}
                        placeholder={`Ask ${build?.name} anything...`}
                        onKeyDown={async e => {
                          if (e.key === 'Enter' && !e.shiftKey && chatInput.trim() && !chatLoading) {
                            e.preventDefault();
                            const msg = chatInput.trim();
                            setChatInput('');
                            setChatMessages(prev => [...prev, { role: 'user', content: msg }]);
                            setChatLoading(true);
                            setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
                            try {
                              const history = chatMessages.map(m => ({ role: m.role, content: m.content }));
                              const res = await fetch(`${API_BASE}/v1/builds/${slug}/chat`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ message: msg, history }),
                              });
                              const data = await res.json();
                              setChatMessages(prev => [...prev, {
                                role: 'assistant', content: data.reply || data.error || 'No response',
                                tool_used: data.tool_used, tool_name: data.tool_name,
                                cost: data.cost, raw_data: data.raw_data, showRaw: false,
                              }]);
                            } catch (err) {
                              setChatMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }]);
                            }
                            setChatLoading(false);
                            setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                          }
                        }}
                        style={{
                          flex: 1, padding: '10px 14px', borderRadius: 'var(--radius-xs)',
                          border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)',
                          fontSize: 13, outline: 'none',
                        }}
                      />
                      <button
                        disabled={chatLoading || !chatInput.trim()}
                        onClick={async () => {
                          if (!chatInput.trim() || chatLoading) return;
                          const msg = chatInput.trim();
                          setChatInput('');
                          setChatMessages(prev => [...prev, { role: 'user', content: msg }]);
                          setChatLoading(true);
                          setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
                          try {
                            const history = chatMessages.map(m => ({ role: m.role, content: m.content }));
                            const res = await fetch(`${API_BASE}/v1/builds/${slug}/chat`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ message: msg, history }),
                            });
                            const data = await res.json();
                            setChatMessages(prev => [...prev, {
                              role: 'assistant', content: data.reply || data.error || 'No response',
                              tool_used: data.tool_used, tool_name: data.tool_name,
                              cost: data.cost, raw_data: data.raw_data, showRaw: false,
                            }]);
                          } catch (err) {
                            setChatMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }]);
                          }
                          setChatLoading(false);
                          setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
                        }}
                        style={{
                          padding: '10px 20px', borderRadius: 'var(--radius-xs)', border: 'none',
                          background: chatLoading ? 'var(--border)' : 'linear-gradient(135deg, var(--accent), #6558d4)',
                          color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                        }}
                      >
                        {chatLoading ? '...' : 'Send'}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── JSON mode (developer) ── */}
                {pgMode === 'json' && (
                  <>
                    <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.6 }}>
                      Direct API calls. <strong style={{ color: 'var(--accent2)' }}>Credits charged</strong> for successful calls.
                    </div>
                    {hasTools && (
                      <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4, display: 'block' }}>Tool</label>
                        <select value={pgTool} onChange={e => { setPgTool(e.target.value); const t = tools.find(t => t.slug === e.target.value); if (t?.command) setPgBody(t.command); }}
                          style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-xs)', border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13 }}>
                          <option value="">Base endpoint</option>
                          {tools.map(t => <option key={t.slug} value={t.slug}>{t.name} — ${t.price_per_call?.toFixed(2)}/call</option>)}
                        </select>
                      </div>
                    )}
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4, display: 'block' }}>Request Body (JSON)</label>
                      <textarea value={pgBody} onChange={e => setPgBody(e.target.value)} rows={6}
                        style={{ width: '100%', padding: '12px', borderRadius: 'var(--radius-xs)', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--accent2)', fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: 12.5, lineHeight: 1.7, resize: 'vertical' }} />
                    </div>
                    <button disabled={pgRunning || !isSignedIn()} onClick={async () => {
                      setPgRunning(true); setPgError(''); setPgResult(null);
                      try {
                        let body; try { body = JSON.parse(pgBody); } catch { setPgError('Invalid JSON'); setPgRunning(false); return; }
                        const headers = { 'Content-Type': 'application/json' };
                        if (pgTool) headers['x-invokeapi-tool'] = pgTool;
                        const res = await authFetch(`/v1/call/${slug}`, { method: 'POST', headers, body: JSON.stringify(body) });
                        const text = await res.text();
                        let parsed; try { parsed = JSON.parse(text); } catch { parsed = text; }
                        setPgResult({ status: res.status, data: parsed, credits_charged: res.headers.get('x-agentmarket-credits-charged'), credits_remaining: res.headers.get('x-agentmarket-credits-remaining'), cached: res.headers.get('x-cache') === 'HIT' });
                      } catch (e) { setPgError(e.message); }
                      setPgRunning(false);
                    }} style={{ padding: '10px 28px', borderRadius: 'var(--radius-sm)', border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer', background: pgRunning ? 'var(--border)' : 'linear-gradient(135deg, var(--accent), #6558d4)', color: '#fff', boxShadow: pgRunning ? 'none' : '0 2px 12px rgba(124,111,239,0.3)', opacity: !isSignedIn() ? 0.5 : 1 }}>
                      {pgRunning ? 'Running...' : 'Send Request'}
                    </button>
                    {!isSignedIn() && <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 12 }}><span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navigate('signin')}>Sign in</span> to use</span>}
                  </>
                )}

                {pgError && (
                  <div style={{ marginTop: 16, padding: '12px 16px', borderRadius: 'var(--radius-sm)', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: 13 }}>
                    {pgError}
                  </div>
                )}

                {pgResult && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, background: pgResult.status < 300 ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)', color: pgResult.status < 300 ? 'var(--green)' : '#f87171' }}>{pgResult.status}</span>
                      {pgResult.cached && <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 500, background: 'rgba(234,179,8,0.15)', color: '#facc15' }}>CACHED</span>}
                      {pgResult.credits_charged && <span style={{ fontSize: 12, color: 'var(--muted)' }}>Charged: <strong style={{ color: 'var(--accent2)' }}>${parseFloat(pgResult.credits_charged).toFixed(4)}</strong></span>}
                      {pgResult.credits_remaining && <span style={{ fontSize: 12, color: 'var(--muted)' }}>Balance: <strong>${parseFloat(pgResult.credits_remaining).toFixed(2)}</strong></span>}
                    </div>
                    <div style={{ ...s.codeBlock, maxHeight: 400, overflow: 'auto', position: 'relative' }}>
                      <button style={s.copyBtn} onClick={() => copy(JSON.stringify(pgResult.data, null, 2), 'result')}>{copied === 'result' ? 'Copied!' : 'Copy'}</button>
                      {typeof pgResult.data === 'string' ? pgResult.data : JSON.stringify(pgResult.data, null, 2)}
                    </div>
                  </div>
                )}
              </div>

              {/* Pulse animation for chat loading dots */}
              <style>{`@keyframes pulse { 0%, 100% { opacity: 0.3; transform: scale(1); } 50% { opacity: 1; transform: scale(1.2); } }`}</style>
            </>
          )}

          {/* ── Ratings tab ── */}
          {tab === 'ratings' && (
            <>
              <div style={s.section}>
                <div style={s.sectionTitle}>Ratings & Reviews</div>

                {/* Summary */}
                {build.rating_summary?.count > 0 ? (
                  <div style={{ display: 'flex', gap: 32, alignItems: 'center', marginBottom: 28, padding: '20px 24px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 42, fontWeight: 700, color: 'var(--accent2)', lineHeight: 1 }}>{build.rating_summary.avg_rating}</div>
                      <div style={{ fontSize: 20, marginTop: 4 }}>
                        {[1,2,3,4,5].map(i => <span key={i} style={{ color: i <= Math.round(build.rating_summary.avg_rating) ? '#facc15' : 'var(--border)' }}>{'\u2605'}</span>)}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{build.rating_summary.count} review{build.rating_summary.count !== 1 ? 's' : ''}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                      {[5,4,3,2,1].map(n => {
                        const count = build.rating_summary[['','one','two','three','four','five'][n]] || 0;
                        const pct = build.rating_summary.count > 0 ? (count / build.rating_summary.count) * 100 : 0;
                        return (
                          <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                            <span style={{ fontSize: 12, color: 'var(--muted)', width: 12, textAlign: 'right' }}>{n}</span>
                            <span style={{ color: '#facc15', fontSize: 12 }}>{'\u2605'}</span>
                            <div style={{ flex: 1, height: 6, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: '#facc15', borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 11, color: 'var(--muted)', width: 20 }}>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--muted)', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 28 }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>{'\u2605'}</div>
                    <div style={{ fontWeight: 500, color: 'var(--text)', marginBottom: 4 }}>No reviews yet</div>
                    <div style={{ fontSize: 13 }}>Be the first to rate this build after using it.</div>
                  </div>
                )}

                {/* Leave a rating */}
                {session ? (
                  myCallCount > 0 ? (
                    <div style={{ padding: 20, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 28 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
                        {myRating ? 'Update your review' : 'Rate this build'}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
                        You've made {myCallCount} successful call{myCallCount !== 1 ? 's' : ''} to this build.
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        {[1,2,3,4,5].map(i => (
                          <span key={i} onClick={() => setRatingForm(f => ({ ...f, rating: i }))}
                                style={{ fontSize: 28, cursor: 'pointer', color: i <= ratingForm.rating ? '#facc15' : 'var(--border)', transition: 'color 0.1s' }}>
                            {'\u2605'}
                          </span>
                        ))}
                      </div>
                      <textarea
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, outline: 'none', resize: 'vertical', minHeight: 60, boxSizing: 'border-box', marginBottom: 10 }}
                        placeholder="Optional review — what did you think?"
                        value={ratingForm.review} onChange={e => setRatingForm(f => ({ ...f, review: e.target.value }))}
                        maxLength={1000}
                      />
                      {ratingError && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 8 }}>{ratingError}</div>}
                      <button onClick={submitRating} disabled={!ratingForm.rating || ratingSubmitting}
                              style={{ padding: '8px 20px', borderRadius: 'var(--radius-sm)', border: 'none', background: ratingForm.rating ? 'var(--accent)' : 'var(--bg3)', color: ratingForm.rating ? '#fff' : 'var(--muted)', fontWeight: 600, fontSize: 13, cursor: ratingForm.rating ? 'pointer' : 'default' }}>
                        {ratingSubmitting ? 'Saving...' : myRating ? 'Update Review' : 'Submit Review'}
                      </button>
                    </div>
                  ) : (
                    <div style={{ padding: '16px 20px', background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.2)', borderRadius: 'var(--radius-sm)', marginBottom: 28, fontSize: 13, color: 'var(--muted)' }}>
                      {'\u{1F512}'} You need to make at least one successful call to this build before you can rate it.
                    </div>
                  )
                ) : (
                  <div style={{ padding: '16px 20px', background: 'rgba(124,111,239,0.06)', border: '1px solid rgba(124,111,239,0.2)', borderRadius: 'var(--radius-sm)', marginBottom: 28, fontSize: 13 }}>
                    <span style={{ cursor: 'pointer', color: 'var(--accent2)', fontWeight: 600 }} onClick={() => navigate('signin')}>Sign in</span>
                    <span style={{ color: 'var(--muted)' }}> to rate this build (verified users only).</span>
                  </div>
                )}

                {/* Individual reviews */}
                {(build.ratings || []).map(r => (
                  <div key={r.id} style={{ padding: '16px 18px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{r.consumer_name}</span>
                        <span style={{ fontSize: 11, color: 'var(--green)', background: 'rgba(34,197,94,0.1)', padding: '1px 8px', borderRadius: 99 }}>
                          {r.call_count} call{r.call_count !== 1 ? 's' : ''} verified
                        </span>
                      </div>
                      <div>
                        {[1,2,3,4,5].map(i => <span key={i} style={{ fontSize: 14, color: i <= r.rating ? '#facc15' : 'var(--border)' }}>{'\u2605'}</span>)}
                      </div>
                    </div>
                    {r.review && <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>{r.review}</div>}
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>{new Date(r.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Sidebar */}
        <div style={s.sidebar}>
          {hasTools && build.price_range ? (
            <>
              <div style={s.price}>${build.price_range.min.toFixed(2)} – ${build.price_range.max.toFixed(2)}</div>
              <div style={s.priceSub}>per call (depends on tool)</div>
            </>
          ) : (
            <>
              <div style={s.price}>${build.price_per_call?.toFixed(2)}</div>
              <div style={s.priceSub}>per API call</div>
            </>
          )}

          <button style={s.callBtn} onClick={() => navigate('getkey')}>
            Get API key
          </button>
          {storedConsumerId && (
            <button style={s.secondBtn} onClick={() => navigate('consumer')}>
              My Account
            </button>
          )}

          <div style={s.divider} />

          {build.rating_summary?.count > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                {[1,2,3,4,5].map(i => <span key={i} style={{ fontSize: 16, color: i <= Math.round(build.rating_summary.avg_rating) ? '#facc15' : 'var(--border)' }}>{'\u2605'}</span>)}
                <span style={{ fontWeight: 600, fontSize: 15, marginLeft: 4 }}>{build.rating_summary.avg_rating}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14, cursor: 'pointer' }} onClick={() => setTab('ratings')}>
                {build.rating_summary.count} verified review{build.rating_summary.count !== 1 ? 's' : ''}
              </div>
            </>
          )}

          {/* Trust badges */}
          {build.badge && (
            <div style={{
              display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14,
            }}>
              {build.badge.split(',').map(b => b.trim()).filter(Boolean).map(badge => {
                const badges = {
                  power_builder: { label: 'Power Builder', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
                  fast: { label: 'Fast', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
                  reliable: { label: 'Reliable', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
                  verified: { label: 'Verified', color: '#a855f7', bg: 'rgba(168,85,247,0.12)' },
                };
                const info = badges[badge] || { label: badge, color: 'var(--muted)', bg: 'var(--bg3)' };
                return (
                  <span key={badge} style={{
                    padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                    background: info.bg, color: info.color, border: `1px solid ${info.color}22`,
                  }}>
                    {info.label}
                  </span>
                );
              })}
            </div>
          )}

          {/* Verification tier */}
          {verification && verification.verification_tier > 0 && (() => {
            const tier = verification.verification_tier;
            const tierColors = ['var(--muted)', '#eab308', '#7c6fef', '#22c55e'];
            const tierLabels = ['', 'Auto-Verified', 'Reviewed', 'Track Record'];
            const tierIcons = ['', '✓', '✓✓', '★'];
            return (
              <div style={{ marginBottom: 14 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  borderRadius: 'var(--radius-sm)', background: `${tierColors[tier]}11`,
                  border: `1px solid ${tierColors[tier]}33`,
                }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700,
                    background: `${tierColors[tier]}22`, color: tierColors[tier],
                  }}>{tierIcons[tier]}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: tierColors[tier] }}>
                      Tier {tier} — {tierLabels[tier]}
                    </div>
                    {verification.tier2?.models_used && (
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                        Models: {verification.tier2.models_used}
                      </div>
                    )}
                  </div>
                </div>
                {verification.tier2?.data_sources && (
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, paddingLeft: 2 }}>
                    Data: {verification.tier2.data_sources}
                  </div>
                )}
                {verification.tier2?.privacy_stance && (
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, paddingLeft: 2 }}>
                    Privacy: {verification.tier2.privacy_stance.replace(/_/g, ' ')}
                  </div>
                )}
              </div>
            );
          })()}

          <div style={s.statRow}>
            <span style={s.statLabel}>Status</span>
            <StatusDot isLive={build.is_live} uptime={build.uptime_pct} />
          </div>
          <div style={s.statRow}>
            <span style={s.statLabel}>Uptime</span>
            <span style={s.statVal}>{build.uptime_pct?.toFixed(1) ?? 100}%</span>
          </div>
          {hasTools && (
            <div style={s.statRow}>
              <span style={s.statLabel}>Tools</span>
              <span style={s.statVal}>{tools.length}</span>
            </div>
          )}
          {hasJobs && (
            <div style={s.statRow}>
              <span style={s.statLabel}>Jobs</span>
              <span style={s.statVal}>{jobTypes.length}</span>
            </div>
          )}
          <div style={s.statRow}>
            <span style={s.statLabel}>Base price</span>
            <span style={s.statVal}>${build.price_per_call?.toFixed(2)}/call</span>
          </div>
          <div style={s.statRow}>
            <span style={s.statLabel}>Last ping</span>
            <span style={{ fontSize: 12 }}>
              {build.last_ping_at ? new Date(build.last_ping_at + 'Z').toLocaleTimeString() : '\u2014'}
            </span>
          </div>

          <div style={s.divider} />

          {build.max_request_bytes && (
            <div style={s.statRow}>
              <span style={s.statLabel}>Max upload</span>
              <span style={{ fontSize: 12 }}>{(build.max_request_bytes / 1048576).toFixed(0)}MB</span>
            </div>
          )}

          <div style={{
            fontSize: 12, color: 'var(--muted)', lineHeight: 1.7,
            background: 'var(--bg3)', padding: '10px 12px',
            borderRadius: 'var(--radius-xs)',
          }}>
            {hasTools
              ? 'Each tool has its own price. Send the x-invokeapi-tool header to specify which tool you\'re calling.'
              : 'Credits deducted on success only. Timeouts and errors are not charged.'}
            {' '}Supports JSON, binary, images, video, and audio content.
          </div>
        </div>
      </div>
    </div>
  );
}
